/**
 * NewPortfolioModal — create a portfolio.
 *
 * Mirrors the NewProjectModal pattern: a single-modal form with required
 * + optional fields, validation, optimistic store add, navigate-on-create.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea, Label } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useProjectStore } from '@/store/useProjectStore';
import { toast } from '@/store/useToastStore';
import type { Portfolio, PortfolioStatus } from '@/types';

interface NewPortfolioModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** PORT-XXXXXX (6 alphanumerics, ~2B combinations). */
function generatePortfolioId(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = 'PORT-';
  for (let i = 0; i < 6; i += 1) {
    id += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return id;
}

export function NewPortfolioModal({ isOpen, onClose }: NewPortfolioModalProps) {
  const navigate = useNavigate();
  const addPortfolio = useProjectStore((s) => s.addPortfolio);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [manager, setManager] = useState('');
  const [status, setStatus] = useState<PortfolioStatus>('active');
  const [targetBudget, setTargetBudget] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setDescription('');
    setManager('');
    setStatus('active');
    setTargetBudget('');
    setSubmitting(false);
  }, [isOpen]);

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= 100;
  const budgetValid =
    targetBudget === '' ||
    (Number(targetBudget) >= 0 && !Number.isNaN(Number(targetBudget)));
  const formValid = nameValid && budgetValid && !submitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!formValid) return;
    setSubmitting(true);

    const newPortfolio: Portfolio = {
      id: generatePortfolioId(),
      name: trimmedName,
      description: description.trim(),
      manager: manager.trim(),
      status,
      startDate: null,
      endDate: null,
      targetBudget: targetBudget === '' ? 0 : Number(targetBudget),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    addPortfolio(newPortfolio);
    toast.success(`Portfolio "${newPortfolio.name}" created`);
    onClose();
    navigate(`/portfolios/${newPortfolio.id}`);
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="accent"
        onClick={handleSubmit}
        disabled={!formValid}
        isLoading={submitting}
      >
        Create portfolio
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New portfolio"
      size="lg"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="np-name" required>
            Portfolio name
          </Label>
          <Input
            id="np-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Digital Transformation, FY26 Strategic Initiatives"
            autoFocus
            maxLength={100}
            disabled={submitting}
          />
          {name.length > 0 && !nameValid && (
            <div className="text-xs text-danger-dark mt-1">
              Name must be 1–100 characters
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="np-description">Description</Label>
          <Textarea
            id="np-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this portfolio about?"
            rows={2}
            disabled={submitting}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="np-manager">Manager</Label>
            <Input
              id="np-manager"
              value={manager}
              onChange={(e) => setManager(e.target.value)}
              placeholder="e.g. Jane Doe, PMO Director"
              disabled={submitting}
            />
          </div>
          <div>
            <Label htmlFor="np-status">Status</Label>
            <Select
              id="np-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as PortfolioStatus)}
              disabled={submitting}
            >
              <option value="active">Active</option>
              <option value="on-hold">On hold</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="np-budget">Target budget (USD)</Label>
          <Input
            id="np-budget"
            type="number"
            min={0}
            step={10000}
            value={targetBudget}
            onChange={(e) => setTargetBudget(e.target.value)}
            placeholder="0"
            disabled={submitting}
          />
          {targetBudget !== '' && !budgetValid && (
            <div className="text-xs text-danger-dark mt-1">
              Budget must be a non-negative number
            </div>
          )}
        </div>

        <p className="text-xs text-ink-tertiary pt-2 border-t border-line-light">
          You can add projects to the portfolio after creating it. Health, dates, completion,
          and budget metrics roll up automatically from the projects you assign.
        </p>
      </form>
    </Modal>
  );
}
